/**
 * A barra de atalhos do painel: cinco pastilhas que pulam para uma seção.
 *
 * É o `_DashboardJumpBar` de `lib/features/dashboard/presentation/screens/
 * dashboard_screen.dart` (1314-1481), com o `_JumpChip` junto — lá também são
 * duas classes no mesmo arquivo, porque uma só existe dentro da outra.
 *
 * As palavras das pastilhas são as de lá, em inglês (`Today`, `Watching`,
 * `Shelves`, `Timeline`, `Moments`); o cabeçalho e as etiquetas de leitor de
 * tela já estão em português no Flutter (`IR PARA`, `Ir para uma seção`).
 *
 * O que o navegador obrigou a mudar:
 *
 * - **A âncora é um `id`, não uma `GlobalKey`.** Lá cada destino é uma caixa
 *   invisível com uma chave, e o `Scrollable.ensureVisible` acha o contexto
 *   dela. Aqui é a mesma caixa invisível com um `id`, e quem rola é o
 *   `scrollIntoView` — a ferramenta que a plataforma já tem.
 * - **O `alignment: 0.05` virou `scroll-margin-top`.** Não há como pedir "para
 *   a 5% do alto" ao `scrollIntoView`; a folga vai na âncora, que é onde o CSS
 *   sabe respeitá-la.
 * - **Quem não anda menos é decidido aqui, no JavaScript.** O
 *   `prefers-reduced-motion` do `globais.css` desliga o `scroll-behavior`, mas
 *   um `behavior` passado à mão vence a folha de estilo. Então a pergunta é
 *   feita de novo aqui, como no `CartaoDoJardim` e no `PreviaDoPote`.
 * - **A pastilha some quando a seção está desligada.** Lá as cinco aparecem
 *   sempre, e o `_jumpTo` não faz nada quando a chave não achou contexto — um
 *   botão que não vai a lugar nenhum. É o mesmo defeito da setinha morta de
 *   "Assistindo agora", e aqui dá para saber antes de desenhar.
 * - **"Shelves" para nas prateleiras, e não depois delas.** Lá a âncora está
 *   *depois* do `BooksPreview`, então a pastilha do cinema leva à galeria. Além
 *   de ser um engano de lá, esta casa não tem a ordem de lá: livros e álbum
 *   ficam no alto, cinema fica embaixo. Cada pastilha aponta para a seção que
 *   ela nomeia, na ordem desta casa.
 * - **Sem o `MouseRegion`.** O clarão do dedo por cima é `:hover` no CSS, que
 *   de quebra atende quem chega pelo teclado com o `:focus-visible`.
 */

import { ligado, traduzir, usarAjustes, type NomeDoIcone } from '@nosso-sempre/shared';
import { Icone } from './Icone';
import './barraDePulo.css';

interface Alvo {
  /** A palavra da pastilha, a de lá. */
  nome: string;
  icone: NomeDoIcone;
  /** O `hue` do `_JumpItem`: pinta a borda, o fundo e o ícone. */
  cor: string;
  /** O nome da âncora, o mesmo do `_sectionKeys` de lá. */
  id: string;
  /** A chave do recurso que liga a seção de destino. Sem ela, nunca desliga. */
  recurso?: string;
}

const ALVOS: readonly Alvo[] = [
  { nome: 'Today', icone: 'brilho', cor: 'var(--aurora-ouro)', id: 'coming-up' },
  {
    nome: 'Watching',
    icone: 'tocar-circulo',
    cor: 'var(--aurora-rosa)',
    id: 'watching',
    recurso: 'cinema',
  },
  { nome: 'Shelves', icone: 'cinema', cor: 'var(--lavanda)', id: 'shelves', recurso: 'cinema' },
  { nome: 'Timeline', icone: 'linha-do-tempo', cor: 'var(--ouro)', id: 'timeline' },
  { nome: 'Moments', icone: 'coracao', cor: 'var(--rosa)', id: 'moments', recurso: 'starlightJar' },
];

/**
 * A caixa invisível que marca onde uma seção começa.
 *
 * É a `GlobalKey` do `_sectionKeys` virada gente: não desenha nada, não ocupa
 * lugar, só dá um endereço para a barra mirar. A folga do `alignment: 0.05`
 * mora no CSS dela.
 */
export function Ancora({ id }: { id: string }) {
  return <span className="pulo-ancora" id={`pulo-${id}`} aria-hidden="true" />;
}

/** O `_jumpTo`: acha a âncora e rola até ela, ou não faz nada. */
function pular(id: string) {
  const onde = document.getElementById(`pulo-${id}`);
  if (!onde) return;
  const menos = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  onde.scrollIntoView({ behavior: menos ? 'auto' : 'smooth', block: 'start' });
}

export function BarraDePulo() {
  const ajustes = usarAjustes();
  const alvos = ALVOS.filter((a) => !a.recurso || ligado(ajustes, a.recurso));

  return (
    <nav className="pulo" aria-label={traduzir('Ir para uma seção')}>
      <p className="pulo-tarja">
        <span className="pulo-fio curto" aria-hidden="true" />
        <span className="pulo-selo">{traduzir('IR PARA')}</span>
        <span className="pulo-fio" aria-hidden="true" />
      </p>

      <ul className="pulo-fichas">
        {alvos.map((alvo) => (
          <li key={alvo.id}>
            <button
              type="button"
              className="pulo-ficha"
              style={{ ['--cor-pulo' as string]: alvo.cor }}
              aria-label={traduzir('Ir para {nome}', { nome: alvo.nome })}
              onClick={() => pular(alvo.id)}
            >
              <span className="pulo-bolha" aria-hidden="true">
                <Icone nome={alvo.icone} tamanho={12} />
              </span>
              {alvo.nome}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}
